import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { Button } from '../../ui/Button/Button'
import { Input } from '../../ui/Input/Input'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import type { DashboardUser } from '../auth/auth-adapter'
import authStyles from '../auth/LoginPage.module.css'
import { resetContinueUrl, type LoginRouteState } from '../auth/LoginPage'
import {
  getOnboardingApiClient,
  OnboardingApiError,
  type OnboardingApiClient,
} from './onboarding-api'
import type { InvitationPreview } from './organization-onboarding-contracts'

const INVITATION_TOKEN = /^[A-Za-z0-9_-]{43}$/

// The invitation's capability decides only the wording; the server applies its
// fixed Organization (and, for a Clinic Manager, clinic) scope on claim.
function invitationCopy(preview: InvitationPreview) {
  if (preview.capability === 'ORGANIZATION_ADMIN') {
    return {
      scopeName: preview.organizationName,
      invited: `You've been invited to administer ${preview.organizationName}`,
      create:
        'Create your Flio account to manage this organization and all of its clinics as an Administrator.',
      signedIn: `Administer ${preview.organizationName}`,
      applying:
        'The server is applying the invitation’s fixed organization scope.',
    }
  }
  return {
    scopeName: preview.clinicName,
    invited: `You've been invited to manage ${preview.clinicName}`,
    create: 'Create your Flio account to manage this clinic as a Manager.',
    signedIn: `Manage ${preview.clinicName}`,
    applying:
      'The server is applying the invitation’s fixed organization and clinic scope.',
  }
}

function firebaseAuthErrorCode(error: unknown) {
  if (!error || typeof error !== 'object' || !('code' in error)) return null
  return typeof error.code === 'string' ? error.code : null
}

function accountCreationError(error: unknown) {
  switch (firebaseAuthErrorCode(error)) {
    case 'auth/invalid-email':
      return 'Enter a valid email address.'
    case 'auth/operation-not-allowed':
      return 'Account creation is temporarily unavailable. Contact your organization administrator.'
    case 'auth/too-many-requests':
      return 'Firebase temporarily blocked account creation after too many attempts. Wait before trying again.'
    case 'auth/weak-password':
      return 'Choose a stronger password and try again.'
    case 'auth/network-request-failed':
      return 'The account could not be created because the network request failed. Check your connection and try again.'
    default:
      return 'The Firebase account could not be created. No access was granted.'
  }
}

function passwordLinkError(error: unknown) {
  switch (firebaseAuthErrorCode(error)) {
    case 'auth/too-many-requests':
      return 'Too many emails were requested. Wait a few minutes, then try again.'
    case 'auth/network-request-failed':
      return 'The email could not be requested because the network request failed. Check your connection and try again.'
    default:
      return 'The email could not be sent. Try again.'
  }
}

function claimError(error: unknown) {
  if (!(error instanceof OnboardingApiError)) {
    return 'The invitation could not be accepted. Please try again.'
  }
  if (error.code === 'invitation-expired') return 'This invitation has expired.'
  if (error.code === 'invitation-email-mismatch') {
    return 'This invitation belongs to a different email address. Sign in with the invited account.'
  }
  if (
    error.code === 'invitation-invalid' ||
    error.code === 'invitation-conflict'
  ) {
    return 'This invitation is no longer available.'
  }
  return 'The invitation could not be accepted safely. Please try again.'
}

export function ManagerInvitationClaimPage({
  client = getOnboardingApiClient(),
}: {
  client?: OnboardingApiClient
}) {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const validToken = INVITATION_TOKEN.test(token)
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [creatingAccount, setCreatingAccount] = useState(false)
  // Account creation found an account the preview did not report.
  const [existingAccount, setExistingAccount] = useState(false)
  const [passwordLink, setPasswordLink] = useState<'idle' | 'sending' | 'sent'>(
    'idle'
  )
  const [claiming, setClaiming] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const attemptedClaimKey = useRef<string | null>(null)
  const returnPath = `${location.pathname}${location.search}`
  const invitation = useQuery({
    queryKey: ['manager-invitation-preview', token],
    queryFn: () => client.previewManagerInvitation!(token),
    enabled: validToken,
    retry: false,
  })

  const completeInvitation = useCallback(
    async (claimUser: DashboardUser, invitedEmail?: string) => {
      const claimKey = `${claimUser.uid}:${token}`
      attemptedClaimKey.current = claimKey
      setClaiming(true)
      setMessage(null)
      try {
        await client.claimManagerInvitation!(claimUser, token)
        if (!claimUser.emailVerified) {
          try {
            await auth.logout()
          } catch {
            setMessage(
              'Your account and clinic access are ready, but automatic sign-out failed. Sign out before continuing.'
            )
            return
          }
          const loginState: LoginRouteState = {
            email: invitedEmail ?? claimUser.email ?? '',
            message:
              'Your invitation is accepted. Sign in again to refresh your verified account access.',
            reauthenticate: true,
          }
          void navigate('/login', { replace: true, state: loginState })
          return
        }
        void navigate('/', { replace: true })
      } catch (error) {
        setMessage(claimError(error))
      } finally {
        setClaiming(false)
      }
    },
    [auth, client, navigate, token]
  )

  useEffect(() => {
    if (
      auth.status !== 'authenticated' ||
      !validToken ||
      invitation.isPending
    ) {
      return
    }
    const invitedEmail = invitation.data?.email
    if (
      invitedEmail &&
      auth.user.email?.trim().toLowerCase() !== invitedEmail.toLowerCase()
    ) {
      return
    }
    if (
      invitation.data &&
      (!['ACTIVE', 'CLAIMING'].includes(invitation.data.status) ||
        invitation.data.expired)
    ) {
      return
    }
    const claimKey = `${auth.user.uid}:${token}`
    if (attemptedClaimKey.current === claimKey) return
    void completeInvitation(auth.user, invitedEmail)
  }, [
    auth,
    completeInvitation,
    invitation.data,
    invitation.isPending,
    token,
    validToken,
  ])

  if (auth.status === 'hydrating') {
    return <Spinner label="Restoring your account" />
  }
  if (!validToken) {
    return (
      <section className={authStyles.form}>
        <h2>Invitation unavailable</h2>
        <p>This invitation link is incomplete or invalid.</p>
        <Link to="/login">Return to sign in</Link>
      </section>
    )
  }
  if (invitation.isPending) {
    return <Spinner label="Loading your invitation" />
  }
  if (invitation.isError || !invitation.data) {
    if (auth.status === 'unauthenticated') {
      return (
        <section className={authStyles.form}>
          <h2>Continue your invitation</h2>
          <p>Sign in to securely verify and resume this invitation.</p>
          <Link
            to="/login"
            state={
              {
                from: returnPath,
                reauthenticate: true,
              } satisfies LoginRouteState
            }
          >
            Sign in to continue
          </Link>
        </section>
      )
    }
    return (
      <section className={authStyles.form}>
        <h2>Resume your invitation</h2>
        <p>
          The server will verify that this invitation is bound to your account.
        </p>
        {message ? <p role="alert">{message}</p> : null}
        {claiming && !message ? (
          <Spinner label="Completing invitation" />
        ) : null}
        {message ? (
          <Button
            type="button"
            disabled={claiming}
            onClick={() => {
              attemptedClaimKey.current = null
              if (auth.status === 'authenticated') {
                void completeInvitation(auth.user)
              }
            }}
          >
            Try again
          </Button>
        ) : null}
      </section>
    )
  }
  if (
    !['ACTIVE', 'CLAIMING'].includes(invitation.data.status) ||
    invitation.data.expired
  ) {
    return (
      <section className={authStyles.form}>
        <h2>Invitation unavailable</h2>
        <p>This invitation is no longer active.</p>
        <Link to="/login">Return to sign in</Link>
      </section>
    )
  }

  const invitedEmail = invitation.data.email
  const copy = invitationCopy(invitation.data)

  async function createAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (password !== confirmation) {
      setMessage('Passwords do not match.')
      return
    }
    setCreatingAccount(true)
    setMessage(null)
    try {
      await auth.signUp(invitedEmail, password)
    } catch (error) {
      if (firebaseAuthErrorCode(error) === 'auth/email-already-in-use') {
        // The typed password was not saved: say so, and offer the two ways
        // that work for an existing account, on this page.
        setPassword('')
        setConfirmation('')
        setExistingAccount(true)
        setCreatingAccount(false)
        return
      }
      setMessage(accountCreationError(error))
      setCreatingAccount(false)
      return
    }
    setCreatingAccount(false)
  }

  if (
    auth.status === 'unauthenticated' &&
    invitation.data.status === 'CLAIMING'
  ) {
    return (
      <section className={authStyles.form}>
        <h2>Continue your invitation</h2>
        <p>
          Sign in as <strong>{invitedEmail}</strong> to safely resume account
          setup for <strong>{copy.scopeName}</strong>.
        </p>
        <Link
          to="/login"
          state={
            {
              email: invitedEmail,
              from: returnPath,
              reauthenticate: true,
            } satisfies LoginRouteState
          }
        >
          Sign in to continue
        </Link>
      </section>
    )
  }

  // Firebase sends the link; after setting the password, sign-in returns here.
  async function sendPasswordLink() {
    setPasswordLink('sending')
    setMessage(null)
    try {
      await auth.sendPasswordReset(invitedEmail, resetContinueUrl(returnPath))
      setPasswordLink('sent')
    } catch (error) {
      setPasswordLink('idle')
      setMessage(passwordLinkError(error))
    }
  }

  const signInState: LoginRouteState = {
    email: invitedEmail,
    from: returnPath,
    reauthenticate: true,
    message:
      'Sign in with your existing Flio password to accept the invitation. Forgot it? Choose Forgot password? and you will return to this invitation.',
  }
  const accountSetup = existingAccount
    ? 'EXISTING_ACCOUNT'
    : (invitation.data.accountSetup ?? 'CREATE_ACCOUNT')

  if (auth.status === 'unauthenticated' && passwordLink === 'sent') {
    return (
      <section className={authStyles.form}>
        <h2>Check your email</h2>
        <p>
          We sent a link to <strong>{invitedEmail}</strong> to set your
          password. It comes from Flio’s sign-in service
          (noreply@flio-heygent-dev.firebaseapp.com); check your spam folder if
          you don’t see it within a few minutes.
        </p>
        <p>
          After you set your password, sign in with it and your invitation to{' '}
          <strong>{copy.scopeName}</strong> completes.
        </p>
        <Link to="/login" state={signInState}>
          I’ve set my password — sign in
        </Link>
        <Button
          type="button"
          variant="ghost"
          onClick={() => void sendPasswordLink()}
        >
          Send the email again
        </Button>
      </section>
    )
  }

  if (auth.status === 'unauthenticated' && accountSetup === 'UNAVAILABLE') {
    return (
      <section className={authStyles.form}>
        <h2>This invitation can’t be accepted yet</h2>
        <p>
          The Flio account for <strong>{invitedEmail}</strong> is disabled.
          Contact the person who invited you.
        </p>
      </section>
    )
  }

  if (auth.status === 'unauthenticated' && accountSetup === 'SET_PASSWORD') {
    return (
      <section className={authStyles.form}>
        <h2>{copy.invited}</h2>
        <p>
          A Flio account is ready for <strong>{invitedEmail}</strong>, but it
          doesn’t have a password yet. We’ll email you a secure link to set one;
          then you sign in and the invitation completes.
        </p>
        {message ? <p role="alert">{message}</p> : null}
        <Button
          className={authStyles.submit}
          type="button"
          disabled={passwordLink === 'sending'}
          onClick={() => void sendPasswordLink()}
        >
          {passwordLink === 'sending'
            ? 'Sending…'
            : 'Email me a link to set my password'}
        </Button>
      </section>
    )
  }

  if (
    auth.status === 'unauthenticated' &&
    (accountSetup === 'SIGN_IN' || accountSetup === 'EXISTING_ACCOUNT')
  ) {
    return (
      <section className={authStyles.form}>
        <h2>{copy.invited}</h2>
        <p>
          {accountSetup === 'EXISTING_ACCOUNT' ? (
            <>
              <strong>{invitedEmail}</strong> already has a Flio account, so the
              password you entered was not saved.
            </>
          ) : (
            <>
              <strong>{invitedEmail}</strong> already has a Flio account.
            </>
          )}{' '}
          Sign in with your existing Flio password to accept, or set a new
          password by email.
        </p>
        {message ? <p role="alert">{message}</p> : null}
        <Link className={authStyles.submit} to="/login" state={signInState}>
          Sign in to accept
        </Link>
        <Button
          type="button"
          variant="ghost"
          disabled={passwordLink === 'sending'}
          onClick={() => void sendPasswordLink()}
        >
          {passwordLink === 'sending'
            ? 'Sending…'
            : 'Email me a link to set a new password'}
        </Button>
      </section>
    )
  }

  if (auth.status === 'unauthenticated') {
    return (
      <form
        className={authStyles.form}
        onSubmit={(event) => void createAccount(event)}
      >
        <h2>{copy.invited}</h2>
        <p>{copy.create}</p>
        <div className={authStyles.fields}>
          <div className={authStyles.staticField}>
            <span>Invited email</span>
            <strong>{invitedEmail}</strong>
          </div>
          <Input
            id="manager-password"
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Input
            id="manager-password-confirmation"
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </div>
        {message ? <p role="alert">{message}</p> : null}
        <Button
          className={authStyles.submit}
          type="submit"
          disabled={creatingAccount || claiming}
        >
          {creatingAccount ? 'Creating account…' : 'Create account'}
        </Button>
        <p className={authStyles.secondaryAction}>
          Already have a Flio account?{' '}
          <Link
            to="/login"
            state={
              {
                email: invitedEmail,
                from: returnPath,
                reauthenticate: true,
              } satisfies LoginRouteState
            }
          >
            Sign in
          </Link>
        </p>
      </form>
    )
  }

  if (auth.user.email?.trim().toLowerCase() !== invitedEmail) {
    return (
      <section className={authStyles.form}>
        <h2>Use the invited account</h2>
        <p>
          This invitation was sent to <strong>{invitedEmail}</strong>, but you
          are signed in as <strong>{auth.user.email}</strong>.
        </p>
        <Button
          type="button"
          variant="ghost"
          disabled={claiming}
          onClick={() => void auth.logout()}
        >
          Use another account
        </Button>
      </section>
    )
  }

  return (
    <section className={authStyles.form}>
      <h2>{copy.signedIn}</h2>
      <p>
        Signed in as <strong>{auth.user.email}</strong>. {copy.applying}
      </p>
      {message ? <p role="alert">{message}</p> : null}
      {claiming && !message ? <Spinner label="Completing invitation" /> : null}
      {message ? (
        <Button
          type="button"
          disabled={claiming}
          onClick={() => {
            attemptedClaimKey.current = null
            void completeInvitation(auth.user, invitedEmail)
          }}
        >
          Try again
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        disabled={claiming}
        onClick={() => void auth.logout()}
      >
        Use another account
      </Button>
    </section>
  )
}
